import { createSlice } from "@reduxjs/toolkit";
import {
  getUserEmailHistory,
  getEmailDetails,
  getUserEmailStats,
  getEmailHistoryByCategory,
  sendCustomEmail,
  getEmailCategories
} from "../Action/EmailHistoryAction";

const initialState = {
  // Email history list
  emails: [],
  emailsLoading: false,
  emailsError: null,
  emailsPagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  },

  // Single email details
  selectedEmail: null,
  selectedEmailLoading: false,
  selectedEmailError: null,

  // Email statistics
  emailStats: null,
  emailStatsLoading: false,
  emailStatsError: null,

  // Category emails
  categoryEmails: [],
  categoryEmailsLoading: false,
  categoryEmailsError: null,
  categoryEmailsPagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  },

  // Send custom email
  sendEmailLoading: false,
  sendEmailError: null,
  sendEmailSuccess: false,

  // Email categories
  categories: [],
  categoriesLoading: false,
  categoriesError: null
};

const emailHistorySlice = createSlice({
  name: "emailHistory",
  initialState,
  reducers: {
    clearSelectedEmail: (state) => {
      state.selectedEmail = null;
      state.selectedEmailError = null;
    },
    clearSendEmailStatus: (state) => {
      state.sendEmailLoading = false;
      state.sendEmailError = null;
      state.sendEmailSuccess = false;
    },
    clearCategoryEmails: (state) => {
      state.categoryEmails = [];
      state.categoryEmailsError = null;
      state.categoryEmailsPagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      };
    }
  },
  extraReducers: (builder) => {
    // Get user email history
    builder
      .addCase(getUserEmailHistory.pending, (state) => {
        state.emailsLoading = true;
        state.emailsError = null;
      })
      .addCase(getUserEmailHistory.fulfilled, (state, action) => {
        state.emailsLoading = false;
        state.emails = action.payload.data;
        state.emailsPagination = {
          page: action.payload.page || 1,
          limit: action.payload.limit || 10,
          total: action.payload.total || 0,
          totalPages: Math.ceil((action.payload.total || 0) / (action.payload.limit || 10))
        };
      })
      .addCase(getUserEmailHistory.rejected, (state, action) => {
        state.emailsLoading = false;
        state.emailsError = action.error.message;
      });

    // Get email details
    builder
      .addCase(getEmailDetails.pending, (state) => {
        state.selectedEmailLoading = true;
        state.selectedEmailError = null;
      })
      .addCase(getEmailDetails.fulfilled, (state, action) => {
        state.selectedEmailLoading = false;
        state.selectedEmail = action.payload;
      })
      .addCase(getEmailDetails.rejected, (state, action) => {
        state.selectedEmailLoading = false;
        state.selectedEmailError = action.error.message;
      });

    // Get user email stats
    builder
      .addCase(getUserEmailStats.pending, (state) => {
        state.emailStatsLoading = true;
        state.emailStatsError = null;
      })
      .addCase(getUserEmailStats.fulfilled, (state, action) => {
        state.emailStatsLoading = false;
        state.emailStats = action.payload;
      })
      .addCase(getUserEmailStats.rejected, (state, action) => {
        state.emailStatsLoading = false;
        state.emailStatsError = action.error.message;
      });

    // Get email history by category
    builder
      .addCase(getEmailHistoryByCategory.pending, (state) => {
        state.categoryEmailsLoading = true;
        state.categoryEmailsError = null;
      })
      .addCase(getEmailHistoryByCategory.fulfilled, (state, action) => {
        state.categoryEmailsLoading = false;
        state.categoryEmails = action.payload.data;
        state.categoryEmailsPagination = {
          page: action.payload.page || 1,
          limit: action.payload.limit || 10,
          total: action.payload.total || 0,
          totalPages: Math.ceil((action.payload.total || 0) / (action.payload.limit || 10))
        };
      })
      .addCase(getEmailHistoryByCategory.rejected, (state, action) => {
        state.categoryEmailsLoading = false;
        state.categoryEmailsError = action.error.message;
      });

    // Send custom email
    builder
      .addCase(sendCustomEmail.pending, (state) => {
        state.sendEmailLoading = true;
        state.sendEmailError = null;
        state.sendEmailSuccess = false;
      })
      .addCase(sendCustomEmail.fulfilled, (state) => {
        state.sendEmailLoading = false;
        state.sendEmailSuccess = true;
      })
      .addCase(sendCustomEmail.rejected, (state, action) => {
        state.sendEmailLoading = false;
        state.sendEmailError = action.error.message;
      });

    // Get email categories
    builder
      .addCase(getEmailCategories.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
      })
      .addCase(getEmailCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categories = action.payload;
      })
      .addCase(getEmailCategories.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = action.error.message;
      });
  }
});

export const { clearSelectedEmail, clearSendEmailStatus, clearCategoryEmails } = emailHistorySlice.actions;
export default emailHistorySlice.reducer;